import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { apiError } from "@/lib/apiError";
import { Loader2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { IDR } from "@/lib/format";
import { AppHeader } from "@/components/AppHeader";
import { inputClass, labelClass, btnPrimary, btnGhost } from "@/components/Brand";
import { AffiliatePanel } from "@/components/AffiliatePanel";
import { PlanPricingList } from "@/components/platform/PlanPricingList";
import { TenantList, tenantBucket } from "@/components/platform/TenantList";
import { AccountsPanel } from "@/components/platform/AccountsPanel";
import { RecentLogins } from "@/components/platform/RecentLogins";
import { EarlyAccessPanel } from "@/components/platform/EarlyAccessPanel";
import { ConfirmDialog } from "@/components/Dialog";
import { SubscriptionOverride } from "@/components/SubscriptionOverride";

const emptyPlan = { name: "", price_idr: 49000, original_price_idr: 149000, interval_months: 1, features: "", active: true };

export default function PlatformAdmin() {
  const { t } = useI18n();
  const [plans, setPlans] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [form, setForm] = useState(emptyPlan);
  const [accounts, setAccounts] = useState([]);
  const [accountQuery, setAccountQuery] = useState("");
  const [tenantToDelete, setTenantToDelete] = useState(null);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [overrideId, setOverrideId] = useState(null);
  const [tenantFilter, setTenantFilter] = useState("all");
  const [planToDelete, setPlanToDelete] = useState(null);
  const [logins, setLogins] = useState([]);

  const deletePlan = async (p) => {
    setPlanToDelete(null);
    try {
      await api.delete(`/platform/plans/${p.plan_id}`);
      await load();
    } catch (err) {
      toast.error(apiError(err, "Could not delete plan"));
    }
  };

  const counts = useMemo(() => ({
    all: tenants.length,
    locked: tenants.filter((tn) => tenantBucket(tn) === "locked").length,
    expiring: tenants.filter((tn) => tenantBucket(tn) === "expiring").length,
    lifetime: tenants.filter((tn) => tenantBucket(tn) === "lifetime").length,
  }), [tenants]);

  const visibleTenants = useMemo(() => {
    const rows = tenantFilter === "all" ? [...tenants] : tenants.filter((tn) => tenantBucket(tn) === tenantFilter);
    return rows.sort((a, b) => {
      const ea = a.subscription?.current_period_end;
      const eb = b.subscription?.current_period_end;
      if (!ea) return 1;
      if (!eb) return -1;
      return new Date(ea) - new Date(eb);
    });
  }, [tenants, tenantFilter]);

  const deleteTenant = async (tn) => {
    setTenantToDelete(null);
    try {
      await api.delete(`/platform/tenants/${tn.tenant_id}`);
      await load();
      toast.success(t("delete_studio"));
    } catch (err) {
      toast.error(apiError(err, "Could not delete"));
    }
  };

  const deleteAccount = async (a) => {
    setAccountToDelete(null);
    try {
      await api.delete(`/platform/accounts/${a.user_id}`);
      await loadAccounts();
      toast.success(t("delete_account"));
    } catch (err) {
      toast.error(apiError(err, "Could not delete"));
    }
  };

  const filteredAccounts = useMemo(() => {
    const q = accountQuery.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((a) =>
      `${a.email} ${a.name || ""} ${a.studio_name || ""}`.toLowerCase().includes(q)
    );
  }, [accounts, accountQuery]);

  const loadAccounts = useCallback(async () => {
    const res = await api.get("/platform/accounts");
    setAccounts(res.data);
  }, []);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [p, tn, r, lg] = await Promise.all([
      api.get("/platform/plans"),
      api.get("/platform/tenants"),
      api.get("/platform/revenue"),
      api.get("/platform/logins?limit=10"),
    ]);
    setPlans(p.data);
    setTenants(tn.data);
    setRevenue(r.data);
    setLogins(lg.data);
    await loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    load().catch(() => toast.error("Platform owner access required"));
  }, [load]);

  const savePlan = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/platform/plans", {
        name: form.name,
        price_idr: Number(form.price_idr),
        original_price_idr: Number(form.original_price_idr),
        interval_months: Number(form.interval_months),
        features: form.features.split(",").map((f) => f.trim()).filter(Boolean),
        active: form.active,
      });
      setForm(emptyPlan);
      await load();
      toast.success(t("save_plan"));
    } catch (err) {
      toast.error(apiError(err, "Could not save plan"));
    } finally {
      setBusy(false);
    }
  };

  const updatePrice = async (plan, price, original, usd) => {
    await api.put(`/platform/plans/${plan.plan_id}`, {
      name: plan.name,
      price_idr: Number(price ?? plan.price_idr),
      original_price_idr: Number(original ?? plan.original_price_idr ?? 0),
      price_usd: Number(usd ?? plan.price_usd ?? 0),
      interval_months: plan.interval_months,
      features: plan.features || [], active: plan.active,
    });
    await load();
    toast.success(t("save_plan"));
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F3F3F1] grain" data-testid="platform-page">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-5 lg:px-12 py-12 lg:py-16 space-y-12">
        <div>
          <p className="eyebrow mb-3">{t("platform")}</p>
          <h1 className="text-4xl sm:text-5xl tracking-tighter font-medium">Seleksa SaaS</h1>
        </div>

        {revenue && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#2A2A2A] border border-[#2A2A2A]" data-testid="platform-metrics">
            <Card label={t("revenue")} value={IDR(revenue.total_idr)} testId="platform-revenue" />
            <Card label="Payments" value={revenue.payment_count} />
            <Card label={t("tenants")} value={revenue.tenant_count} />
            <Card label="Active subs" value={revenue.active_subscriptions} />
          </div>
        )}

        <form onSubmit={savePlan} className="border border-[#2A2A2A] bg-[#0F0F0F] p-7 space-y-6" data-testid="plan-form">
          <p className="eyebrow">{t("plans")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
            <div>
              <label className={labelClass}>{t("plan_name")}</label>
              <input data-testid="plan-name-input" className={inputClass} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div>
              <label className={labelClass}>{t("price")}</label>
              <input data-testid="plan-price-input" type="number" className={inputClass} value={form.price_idr}
                onChange={(e) => setForm({ ...form, price_idr: e.target.value })} required />
            </div>
            <div>
              <label className={labelClass}>{t("original_price")}</label>
              <input data-testid="plan-original-price-input" type="number" className={inputClass} value={form.original_price_idr}
                onChange={(e) => setForm({ ...form, original_price_idr: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>{t("interval_months")}</label>
              <input data-testid="plan-interval-input" type="number" min="1" className={inputClass} value={form.interval_months}
                onChange={(e) => setForm({ ...form, interval_months: e.target.value })} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Features (comma separated)</label>
            <input data-testid="plan-features-input" className={inputClass} value={form.features}
              onChange={(e) => setForm({ ...form, features: e.target.value })} />
          </div>
          <button data-testid="save-plan-btn" disabled={busy} className={btnPrimary}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {t("save_plan")}
          </button>
        </form>

        <PlanPricingList plans={plans} onUpdatePrice={updatePrice} onDelete={setPlanToDelete} />

        <EarlyAccessPanel />

        <TenantList tenants={visibleTenants} counts={counts} filter={tenantFilter}
          onFilter={setTenantFilter} onOverride={setOverrideId} onDelete={setTenantToDelete} />
        <AffiliatePanel />

        <RecentLogins events={logins} />

        <AccountsPanel accounts={filteredAccounts} query={accountQuery}
          onQuery={setAccountQuery} onDelete={setAccountToDelete} />
      </main>

      {overrideId && tenants.some((x) => x.tenant_id === overrideId) && (
        <SubscriptionOverride
          tenant={tenants.find((x) => x.tenant_id === overrideId)}
          plans={plans}
          onClose={() => setOverrideId(null)}
          onSaved={load}
        />
      )}

      {planToDelete && (
        <ConfirmDialog
          testId="plan-delete-dialog"
          title={`${t("remove")} · ${planToDelete.name}`}
          confirmLabel={t("remove")}
          onConfirm={() => deletePlan(planToDelete)}
          onClose={() => setPlanToDelete(null)}
        />
      )}

      {tenantToDelete && (
        <ConfirmDialog
          testId="tenant-delete-dialog"
          title={`${t("delete_studio")} · ${tenantToDelete.name}`}
          body={t("delete_studio_warning")}
          confirmLabel={t("delete_studio")}
          onConfirm={() => deleteTenant(tenantToDelete)}
          onClose={() => setTenantToDelete(null)}
        />
      )}

      {accountToDelete && (
        <ConfirmDialog
          testId="account-delete-dialog"
          title={`${t("delete_account")} · ${accountToDelete.email}`}
          body={t("delete_account_warning")}
          confirmLabel={t("delete_account")}
          onConfirm={() => deleteAccount(accountToDelete)}
          onClose={() => setAccountToDelete(null)}
        />
      )}
    </div>
  );
}

const Card = ({ label, value, testId }) => (
  <div className="bg-[#0B0B0B] p-7" data-testid={testId}>
    <p className="text-[10px] tracking-[0.18em] uppercase text-[#707070]">{label}</p>
    <p className="mt-3 font-mono-accent text-xl">{value}</p>
  </div>
);
